import { useTranslations } from "use-intl";
import {
  type FormikProps,
  FieldArray,
  Form,
  FormikProvider,
  useFormik,
} from "formik";
import { object } from "yup";
import { UserIcon, PersonIcon, XIcon } from "@phosphor-icons/react";
import { Button, TextField } from "@mui/material";

import type { THotelFormValues, TRoom } from "@/app/lib/types/types";
import {
  MAX_CHILDREN_PER_ROOM,
  MAX_VISITORS_PER_ROOM,
} from "@/app/lib/constants";
import PlusMinusButton from "../CounterButton";
import { getRoomsValidationSchema } from "./validation.ts";

function VisitorsSelection<T extends Pick<THotelFormValues, "rooms">>({
  onClose,
  formProps,
}: {
  onClose: () => void;
  formProps: FormikProps<T>;
}) {
  const t = useTranslations();

  const formik = useFormik({
    initialValues: {
      rooms: formProps.values.rooms,
    },
    validationSchema: object().shape({
      rooms: getRoomsValidationSchema(t),
    }),
    onSubmit: (values) => {
      formProps.setFieldValue("rooms", values.rooms);
      onClose();
    },
  });

  const newRoom: TRoom = {
    adults: 1,
    children: 0,
    childrenAges: [],
  };

  const { values, handleBlur } = formik;
  const roomsError = formik?.errors?.rooms as TRoom[] | undefined;
  const roomsTouched = formik?.touched?.rooms as TRoom[] | undefined;

  return (
    <FormikProvider value={formik}>
      <Form noValidate>
        <div className="flex h-full w-full flex-col">
          {/* TODO: show this when enabling multiple rooms booking */}
          <div className="mb-3" style={{ display: "none" }}>
            <h2 className="font-base text-center font-medium text-text-title">
              {t("hotels.search.visitors")}
            </h2>
          </div>

          <FieldArray
            name="rooms"
            render={(arrayHelpers) => (
              <div>
                {/* TODO: show this when enabling multiple rooms booking */}
                <div
                  className="mb-3 flex items-center justify-between border-b border-stroke-neutral-soft py-2"
                  style={{ display: "none" }}
                >
                  <div className="flex">
                    <h4 className="font-medium text-text-primary">
                      {t("hotels.search.rooms")}
                    </h4>
                  </div>

                  <PlusMinusButton
                    value={values.rooms.length}
                    minValue={1}
                    maxValue={5}
                    onChange={(value) =>
                      value < values.rooms.length
                        ? arrayHelpers.remove(value)
                        : arrayHelpers.insert(value, newRoom)
                    }
                  />
                </div>

                {values.rooms?.map((room, roomIndex) => {
                  const maxChildCount = Math.min(
                    MAX_VISITORS_PER_ROOM - room.adults,
                    MAX_CHILDREN_PER_ROOM,
                  );
                  const maxAdultCount = MAX_VISITORS_PER_ROOM - room.children;

                  return (
                    <div
                      key={roomIndex}
                      className="mb-3 w-full border-b border-stroke-neutral-soft"
                    >
                      {/* TODO: show this when enabling multiple rooms booking */}
                      <div
                        className="flex w-full justify-center pb-4"
                        style={{ display: "none" }}
                      >
                        <h2 className="font-medium">
                          {t("hotels.search.room")} #{roomIndex + 1}
                        </h2>
                      </div>
                      <span className="mb-4 inline-block w-full text-xs text-text-tertiary">
                        {t("hotels.search.visitors")}
                      </span>
                      <div className="flex items-center justify-between py-2">
                        <div className="flex items-center">
                          <h4 className="font-medium text-text-primary">
                            {t("hotels.search.adult")}
                          </h4>
                          <UserIcon size={20} className="fill-secondary ml-4" />
                        </div>
                        <PlusMinusButton
                          value={room.adults}
                          onChange={(value) =>
                            arrayHelpers.replace(roomIndex, {
                              ...room,
                              adults: value,
                            })
                          }
                          minValue={1}
                          maxValue={maxAdultCount}
                          minusButtonAriaLabel={t("hotels.search.removeAdult")}
                          plusButtonAriaLabel={t("hotels.search.addAdult")}
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between py-2">
                          <div className="flex items-center">
                            <h4 className="font-medium text-text-primary">
                              {t("hotels.search.child")}
                            </h4>
                            <PersonIcon
                              size={20}
                              className="fill-secondary ml-4"
                            />
                          </div>
                          <PlusMinusButton
                            value={room.children}
                            onChange={(value) => {
                              arrayHelpers.replace(roomIndex, {
                                ...room,
                                children: value,
                                childrenAges: Array.from(
                                  Array(value).keys(),
                                ).map((age, i) => room.childrenAges[i]),
                              });
                            }}
                            maxValue={maxChildCount}
                            minusButtonAriaLabel={t(
                              "hotels.search.removeChild",
                            )}
                            plusButtonAriaLabel={t("hotels.search.addChild")}
                          />
                        </div>
                        {room.children > 0 &&
                          Array.from(Array(room.children).keys()).map(
                            (childIndex) => (
                              <div
                                key={childIndex}
                                className="flex items-center justify-between py-2"
                              >
                                <h4 className="font-medium text-text-primary">
                                  {t("hotels.search.child")} {childIndex + 1}
                                </h4>
                                <TextField
                                  name={`rooms[${roomIndex}].childrenAges[${childIndex}]`}
                                  error={
                                    !!roomsTouched?.at(roomIndex)?.childrenAges[
                                      childIndex
                                    ] &&
                                    !!roomsError?.at(roomIndex)?.childrenAges[
                                      childIndex
                                    ]
                                  }
                                  label={t("hotels.search.childsAge")}
                                  onBlur={handleBlur}
                                  onChange={(e) => {
                                    const numberValue = e.target.value
                                      ? Number(e.target.value)
                                      : undefined;

                                    arrayHelpers.replace(roomIndex, {
                                      ...room,
                                      childrenAges: room.childrenAges.map(
                                        (age, i) =>
                                          // if value is a number, store it as a number, otherwise store it as-is
                                          i === childIndex
                                            ? !numberValue || isNaN(numberValue)
                                              ? e.target.value
                                              : numberValue
                                            : age,
                                      ),
                                    });
                                  }}
                                  type="text"
                                  slotProps={{
                                    htmlInput: {
                                      inputMode: "numeric",
                                      pattern: "d*",
                                    },
                                    input: {
                                      endAdornment: room.childrenAges[
                                        childIndex
                                      ] && (
                                        <button
                                          className="shrink-0"
                                          type="button"
                                          onClick={() => {
                                            formik.setFieldValue(
                                              `rooms[${roomIndex}].childrenAges[${childIndex}]`,
                                              "",
                                            );
                                          }}
                                        >
                                          <XIcon
                                            size={20}
                                            className="fill-text-placeholder"
                                          />
                                        </button>
                                      ),
                                    },
                                  }}
                                  value={room.childrenAges[childIndex]}
                                />
                              </div>
                            ),
                          )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          />

          <Button fullWidth type="submit" variant="contained">
            {t("confirm")}
          </Button>
        </div>
      </Form>
    </FormikProvider>
  );
}

export default VisitorsSelection;
